/**
 * Helper functions for ContactPopup component
 */

import toast from 'react-hot-toast';
import axios from 'axios';
import { checkContactRevealPermission, recordContactReveal } from '../../../../../services/contactRevealService';
import { checkContactApplicantPermission, recordContactApplicant } from '../../../../../services/contactApplicantService';

/**
 * Check if a value exists (not null, undefined, or empty string)
 * 
 * @param {*} value - The value to check
 * @returns {boolean} - Whether the value exists
 */
export const exists = (value) => {
    return value !== null && value !== undefined && value !== '';
};

/**
 * Format location name from location object
 * 
 * @param {Object} location - Location object
 * @returns {string} - Formatted location name
 */
export const formatLocationName = (location) => {
    if (!location) return '';
    
    const { city, state, country } = location;
    
    if (exists(city) && exists(state) && exists(country)) {
        return `${city}, ${state}, ${country}`;
    } else if (exists(city) && exists(country)) {
        return `${city}, ${country}`;
    } else if (exists(state) && exists(country)) {
        return `${state}, ${country}`;
    } else if (exists(city) && exists(state)) {
        return `${city}, ${state}`;
    } else if (exists(city)) {
        return city;
    } else if (exists(state)) {
        return state;
    } else if (exists(country)) {
        return country;
    }
    
    return '';
};

/**
 * Format address information into JSX
 * 
 * @param {Object} addressInfo - Address information
 * @returns {JSX.Element|null} - Formatted address JSX
 */
export const formatAddress = ({
    addressLine1,
    addressLine2,
    city,
    state,
    country,
    zipCode,
    t,
    styles
}) => {
    const addressParts = [];
    
    if (exists(addressLine1)) {
        addressParts.push(addressLine1);
    }
    
    if (exists(addressLine2)) {
        addressParts.push(addressLine2);
    }
    
    const cityStateZip = [];
    if (exists(city)) cityStateZip.push(city);
    if (exists(state)) cityStateZip.push(state);
    if (exists(zipCode)) cityStateZip.push(zipCode);
    
    if (cityStateZip.length > 0) {
        addressParts.push(cityStateZip.join(', '));
    }
    
    if (exists(country)) {
        addressParts.push(country);
    }
    
    if (addressParts.length === 0) return null;
    
    return (
        <div className={styles.addressContainer}>
            {addressParts.map((part, index) => (
                <p key={index} className={styles.addressLine}>{part}</p>
            ))}
        </div>
    );
};

/**
 * Copy text to clipboard and show success message
 * @param {string} text - Text to copy to clipboard
 * @param {Function} t - Translation function
 */
export const copyToClipboard = (text, t) => {
  navigator.clipboard.writeText(text);
  toast.dismiss();
  toast.success(t('ContactPopup.copied_to_clipboard'));
};

/**
 * Handle checking contact reveal permission and recording reveals
 * @param {Object} params - Parameters
 * @param {Object} params.user - Current user object
 * @param {Object} params.talent - Talent data
 * @param {Object} params.features - Subscription features
 * @param {string} params.userToken - User authentication token
 * @param {Function} params.t - Translation function
 * @param {Function} params.setShowContactInfo - Function to show contact info
 * @param {Function} params.setShowUpgradeModal - Function to show upgrade modal
 * @param {Function} params.setUpgradeMessage - Function to set upgrade message
 * @param {Function} params.setShouldShowContactPopup - Function to control contact popup visibility
 * @returns {Promise<boolean>} - Whether contact was successfully revealed
 */
export const handleContactReveal = async ({
  user,
  talent,
  features,
  userToken,
  t,
  setShowContactInfo,
  setShowUpgradeModal,
  setUpgradeMessage,
  setShouldShowContactPopup
}) => {
  // Check if the employer can reveal contact information
  const result = await checkContactRevealPermission({
    employerId: user.id,
    jobseekerId: talent.id,
    features,
    token: userToken
  });
  
  if (result.canReveal) {
    // If it's a new reveal (not already viewed), record it
    if (!result.alreadyRevealed) {
      await recordContactReveal({
        employerId: user.id,
        jobseekerId: talent.id,
        token: userToken
      });
    }
    
    setShowContactInfo(true);
    return true;
  } else {
    // Cannot reveal contact - show upgrade modal
    if (result.reason === 'MONTHLY_LIMIT_REACHED') {
      const limitMessage = t('general.UI.contact_reveal_limit_reached', {
        limit: features.contactReveal
      });
      setUpgradeMessage(limitMessage);
    } else {
      setUpgradeMessage(t('upgrade.subscription_required_for_contacts'));
    }
    
    // Close the contact popup and show the upgrade modal
    setShouldShowContactPopup(false);
    setShowUpgradeModal(true);
    return false;
  }
};

/**
 * Send email to talent
 * @param {Object} params - Parameters
 * @param {Object} params.user - Current user
 * @param {Object} params.talent - Talent data
 * @param {string} params.message - Email message
 * @param {Function} params.setSending - Function to set sending state
 * @param {Function} params.setError - Function to set error state
 * @param {Function} params.setSuccess - Function to set success state
 * @param {Function} params.t - Translation function
 * @param {Object} params.features - Subscription features
 * @param {string} params.userToken - User authentication token
 * @param {Function} params.setShowUpgradeModal - Function to show upgrade modal
 * @param {Function} params.setUpgradeMessage - Function to set upgrade message
 * @param {Function} params.setShouldShowContactPopup - Function to control contact popup visibility
 * @returns {Promise<boolean>} - Whether email was sent successfully
 */
export const sendEmailToTalent = async ({
  user,
  talent,
  message,
  setSending,
  setError,
  setSuccess,
  t,
  features,
  userToken,
  setShowUpgradeModal,
  setUpgradeMessage,
  setShouldShowContactPopup
}) => {
  try {
    setSending(true);
    setError(null);
    
    // First check if the user can contact this applicant based on subscription limits
    if (features && userToken) {
      const result = await checkContactApplicantPermission({
        employerId: user.id,
        jobseekerId: talent.id,
        features,
        token: userToken
      });
      if (!result.canContact) {
        setSending(false);
        
        // Cannot contact applicant - show upgrade modal
        if (result.reason === 'MONTHLY_LIMIT_REACHED') {
          // Try to use the translation key, but with a fallback message
          const fallbackMessage = `You've reached your monthly limit of ${features.contactApplicant} applicant contacts. Please upgrade your subscription to contact more applicants.`;
          const translatedMessage = t('general.UI.contact_applicant_limit_reached', {
            limit: features.contactApplicant,
            defaultValue: fallbackMessage
          });
          setUpgradeMessage(translatedMessage);
        } else {
          setUpgradeMessage(t('upgrade.subscription_required_for_contacts'));
        }
        
        // Close the contact popup and show the upgrade modal
        if (setShouldShowContactPopup) {
          setShouldShowContactPopup(false);
        }
        setShowUpgradeModal(true);
        return false;
      }
      
      // Record the contact if allowed
      if (!result.alreadyContacted) {
        await recordContactApplicant({
          employerId: user.id,
          jobseekerId: talent.id,
          token: userToken
        });
      }
    }
    
    // Send email through the API
    await axios.post(`${process.env.REACT_APP_API_URL}messages/send-email`, {
      employerId: user.id,
      talentId: talent.id,
      message: message,
      replyToEmail: user.email
    });
    
    setSuccess(true);
    setSending(false);
    return true;
  } catch (err) {
    setSending(false);
    setError(t('ContactPopup.error_sending'));
    console.error('Error sending email:', err);
    return false;
  }
};

/**
 * Check if the employer can contact an applicant and handle subscription limits
 * @param {Object} params - Parameters
 * @param {Object} params.user - Current user object
 * @param {Object} params.talent - Talent data
 * @param {Object} params.features - Subscription features
 * @param {string} params.userToken - User authentication token
 * @param {Function} params.t - Translation function
 * @param {Function} params.setShowUpgradeModal - Function to show upgrade modal
 * @param {Function} params.setUpgradeMessage - Function to set upgrade message
 * @param {Function} params.setShouldShowContactPopup - Function to control contact popup visibility
 * @returns {Promise<boolean>} - Whether contact is allowed
 */
export const handleContactApplicantCheck = async ({
  user,
  talent,
  features,
  userToken,
  t,
  setShowUpgradeModal,
  setUpgradeMessage,
  setShouldShowContactPopup
}) => {
  // Check if the employer can contact the applicant
  const result = await checkContactApplicantPermission({
    employerId: user.id,
    jobseekerId: talent.id,
    features,
    token: userToken
  });
  
  if (result.canContact) {
    // If it's a new contact (not already contacted), record it
    if (!result.alreadyContacted) {
      await recordContactApplicant({
        employerId: user.id,
        jobseekerId: talent.id,
        token: userToken
      });
    }
    
    return true;
  } else {
    // Cannot contact applicant - show upgrade modal
    if (result.reason === 'MONTHLY_LIMIT_REACHED') {
      // Use translation with fallback message
      const fallbackMessage = `You've reached your monthly limit of ${features.contactApplicant} applicant contacts. Please upgrade your subscription to contact more applicants.`;
      const translatedMessage = t('general.UI.contact_applicant_limit_reached', {
        limit: features.contactApplicant,
        defaultValue: fallbackMessage
      });
      setUpgradeMessage(translatedMessage);
    } else {
      setUpgradeMessage(t('upgrade.subscription_required_for_contacts'));
    }
    
    // Close the contact popup and show the upgrade modal
    if (setShouldShowContactPopup) {
      setShouldShowContactPopup(false);
    }
    setShowUpgradeModal(true);
    return false;
  }
}; 